import React, { useState } from 'react'
import { LayoutDashboard, Phone, Flame, Bell, ArrowLeftRight, BarChart2, Database, Download, RefreshCw, BookOpen, ListChecks, Copy, LogOut } from 'lucide-react'
import { useData } from './lib/useData'
import { exportAll } from './lib/export'
import { AGENTS, MONTHLY_TARGETS, DATABASE_CONTACTS } from './lib/constants'
import { useAuth } from './lib/AuthContext'
import Dashboard from './components/Dashboard'
import CallLog from './components/CallLog'
import Playbook from './components/Playbook'
import Pipeline from './components/Pipeline'
import MyWorkToday from './components/MyWorkToday'
import DuplicateReview from './components/DuplicateReview'
import LoginPage from './components/LoginPage'
import { Reminders, Distribution, Monthly, Database as DatabaseTab } from './components/OtherTabs'

const ALL_TABS = {
  dashboard:    { label: 'Dashboard',       icon: LayoutDashboard },
  mywork:       { label: 'My Work Today',   icon: ListChecks },
  database:     { label: 'Database',        icon: Database },
  reminders:    { label: 'Reminders',       icon: Bell },
  calllog:      { label: 'Call Log',        icon: Phone },
  pipeline:     { label: 'Pipeline',        icon: Flame },
  distribution: { label: 'AM Distribution', icon: ArrowLeftRight },
  monthly:      { label: 'Monthly',         icon: BarChart2 },
  dupreview:    { label: 'Duplicate Review', icon: Copy, superadminOnly: true },
  playbook:     { label: 'Playbook',        icon: BookOpen },
}
// Superadmin landing = Dashboard; agent landing = My Work Today (spec §2/§9).
const SUPERADMIN_ORDER = ['dashboard', 'mywork', 'database', 'reminders', 'calllog', 'pipeline', 'distribution', 'monthly', 'dupreview', 'playbook']
const AGENT_ORDER      = ['mywork', 'dashboard', 'reminders', 'calllog', 'pipeline', 'distribution', 'monthly', 'database', 'playbook']

// Auth gate: loading -> spinner, logged out -> LoginPage, logged in -> app.
export default function App() {
  const { session } = useAuth()
  if (session === undefined) {
    return (
      <div style={{ minHeight: '100vh', background: '#f5f5f3', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: 14, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
        Memuat...
      </div>
    )
  }
  if (!session) return <LoginPage />
  return <MainApp />
}

function MainApp() {
  const { userName, isSuperadmin, signOut } = useAuth()
  const order = isSuperadmin ? SUPERADMIN_ORDER : AGENT_ORDER
  const tabs = order
    .map(id => ({ id, ...ALL_TABS[id] }))
    .filter(t => !t.superadminOnly || isSuperadmin)
  const [activeTab, setActiveTab] = useState(isSuperadmin ? 'dashboard' : 'mywork')

  const {
    callLogs, pipeline, reminders, dbContacts, tasks, activities, diary, todos,
    loading, error, reload,
    addCallLog, updateCallLog, deleteCallLog,
    addDeal, updateDealStage, updateDeal, deleteDeal,
    addReminder, markReminderDone, deleteReminder,
    addDbContact, addDbContactsBulk, updateDbContact, deleteDbContact,
    addTaskIdempotent, completeTask, cancelTask, addActivity,
    addDiary, updateDiary, deleteDiary,
    addTodo, updateTodo, deleteTodo,
    transferDbContact, checkDuplicate,
  } = useData()

  // ── Role scoping: superadmin sees all, agent sees only own records ────────
  const scope  = (arr) => isSuperadmin ? arr : arr.filter(r => r.agent === userName)
  const scopeT = (arr) => isSuperadmin ? arr : arr.filter(r => r.owner_agent === userName)
  const vCallLogs   = scope(callLogs)
  const vPipeline   = scope(pipeline)
  const vReminders  = scope(reminders)
  const vDbContacts = scope(dbContacts)
  const vTasks      = scopeT(tasks)
  const vActivities = isSuperadmin ? activities : activities.filter(a => a.actor_agent === userName)
  const vDiary      = scope(diary)   // diary_entries.agent = author
  const vTodos      = scope(todos)   // todos.agent = owner
  const exportAgents = isSuperadmin ? AGENTS : [userName]

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
  const overdueCount = vReminders.filter(r => !r.done && r.follow_up_date <= new Date().toISOString().slice(0, 10)).length

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f3', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <div style={{ background: '#fff', borderBottom: '0.5px solid rgba(0,0,0,0.1)', padding: '0 1.5rem', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 56 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="/Logo ICS Hi-Ress.png" alt="ICS Compute" style={{ height: 32, objectFit: 'contain' }} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#111' }}>Telesales Tracker</div>
              <div style={{ fontSize: 11, color: '#888' }}>{today}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 4 }}>
              <span style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>{userName || 'User'}</span>
              <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: isSuperadmin ? '#EAF2FB' : '#E1F5EE', color: isSuperadmin ? '#185FA5' : '#0F6E56' }}>
                {isSuperadmin ? 'DEWA ADMIN' : 'AGENT'}
              </span>
            </div>
            <button onClick={reload} title="Refresh data"
              style={{ background: 'none', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8, padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#666' }}>
              <RefreshCw size={14} />
            </button>
            <button onClick={() => exportAll(vCallLogs, vPipeline, vReminders, exportAgents, MONTHLY_TARGETS, DATABASE_CONTACTS)}
              style={{ background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
              <Download size={14} /> Export{isSuperadmin ? ' All' : ''}
            </button>
            <button onClick={signOut} title="Keluar"
              style={{ background: 'none', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8, padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#A32D2D' }}>
              <LogOut size={14} />
            </button>
          </div>
        </div>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', gap: 0, borderTop: '0.5px solid rgba(0,0,0,0.06)', overflowX: 'auto' }}>
          {tabs.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setActiveTab(id)} style={{
              padding: '10px 14px', fontSize: 13, border: 'none', background: 'none', cursor: 'pointer',
              borderBottom: activeTab === id ? '2px solid #185FA5' : '2px solid transparent',
              color: activeTab === id ? '#185FA5' : '#666', fontWeight: activeTab === id ? 600 : 400,
              display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
            }}>
              <Icon size={14} />
              {label}
              {id === 'reminders' && overdueCount > 0 && (
                <span style={{ background: '#E24B4A', color: '#fff', fontSize: 10, padding: '1px 5px', borderRadius: 99 }}>{overdueCount}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '1.5rem' }}>
        {error && (
          <div style={{ background: '#FCEBEB', color: '#A32D2D', borderRadius: 8, padding: '12px 16px', marginBottom: '1rem', fontSize: 13 }}>
            <strong>Connection error:</strong> {error}. Check your Supabase environment variables.
          </div>
        )}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#888', fontSize: 14 }}>Loading data...</div>
        ) : (
          <>
            {activeTab === 'mywork'       && <MyWorkToday callLogs={vCallLogs} pipeline={vPipeline} reminders={vReminders} dbContacts={vDbContacts} tasks={vTasks} activities={vActivities} diary={vDiary} todos={vTodos} markReminderDone={markReminderDone} completeTask={completeTask} showAgentFilter={isSuperadmin} userName={userName} isSuperadmin={isSuperadmin} addDiary={addDiary} updateDiary={updateDiary} deleteDiary={deleteDiary} addTodo={addTodo} updateTodo={updateTodo} deleteTodo={deleteTodo} />}
            {activeTab === 'dashboard'    && <Dashboard callLogs={vCallLogs} pipeline={vPipeline} reminders={vReminders} dbContacts={vDbContacts} isManager={isSuperadmin} userName={userName} />}
            {activeTab === 'reminders'    && <Reminders reminders={vReminders} addReminder={addReminder} markReminderDone={markReminderDone} deleteReminder={deleteReminder} />}
            {activeTab === 'calllog'      && <CallLog callLogs={vCallLogs} addCallLog={addCallLog} updateCallLog={updateCallLog} deleteCallLog={deleteCallLog} canEdit={true} addActivity={addActivity} addTaskIdempotent={addTaskIdempotent} tasks={vTasks} cancelTask={cancelTask} />}
            {activeTab === 'pipeline'     && <Pipeline pipeline={vPipeline} addDeal={addDeal} updateDealStage={updateDealStage} updateDeal={updateDeal} deleteDeal={deleteDeal} canEdit={true} addActivity={addActivity} />}
            {activeTab === 'distribution' && <Distribution callLogs={vCallLogs} />}
            {activeTab === 'monthly'      && <Monthly callLogs={vCallLogs} agents={isSuperadmin ? undefined : [userName]} />}
            {activeTab === 'database'     && (
              <DatabaseTab
                dbContacts={vDbContacts}
                addDbContact={addDbContact}
                addDbContactsBulk={addDbContactsBulk}
                updateDbContact={updateDbContact}
                deleteDbContact={deleteDbContact}
                addCallLog={addCallLog}
                isSuperadmin={isSuperadmin}
                userName={userName}
                transferDbContact={transferDbContact}
                checkDuplicate={checkDuplicate}
              />
            )}
            {activeTab === 'dupreview'    && isSuperadmin && <DuplicateReview dbContacts={dbContacts} updateDbContact={updateDbContact} deleteDbContact={deleteDbContact} />}
            {activeTab === 'playbook'     && <Playbook />}
          </>
        )}
      </div>
    </div>
  )
}
